import {attach, guard, sample} from 'effector';
import i18next from 'i18next';

import {generatePath} from 'react-router-dom';
import {toast} from 'react-toastify';

import {DocumentStatus} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {stopDocumentInGridPolling, waitDocumentInGridAndRedirectFx} from 'models/documents/create';
import {fetchDataFx} from 'models/documents/documents/grid/table';
import {DocumentsGridRawRowExtended} from 'types/invoices';

import {errorHandlerNoFirs} from '../create/helpers/errorHandlerNoFirs';

import {setInvoiceCreatingPending} from '../details';

import {
    $invoiceState,
    $invoiceStatusInfo,
    $newInvoiceIsDone,
    CreateInvoiceGate,
    deleteInvoiceFx,
    fetchCompanyNameFx,
    fetchOptionFx,
    fetchPaymentMeansFx,
    fetchTaxCategodyFx,
    InvoiceStatusInfo,
    setNewInvoiceIsDone,
    setShowDeleteInvoiceModal,
    updateInvoiceBodyFx,
    updateInvoiceStatusFx,
} from './index';

sample({
    clock: CreateInvoiceGate.close,
    target: stopDocumentInGridPolling,
});

deleteInvoiceFx.fail.watch(({error}) => showRestError(error as RestError));

deleteInvoiceFx.done.watch((data) => {
    const {reportId} = data.result.data;
    setShowDeleteInvoiceModal(false);
    toast.success(i18next.t('pages.documents.form.documentRemoved', {reportId}), {
        className: 'Toastify__toast__wide',
    });
});

const singleInvoiceFetchDataFx = attach({
    effect: fetchDataFx,
    mapParams: () => ({
        sortings: [{id: 'createdAt', desc: true}],
        filters: [],
        paging: {
            pageNumber: 0,
            pageSize: 3,
        },
        search: $invoiceState.getState() ?? '',
        updOnly: true,
    }),
});

guard({
    clock: $invoiceState,
    filter: (i: string | null) => !!i && i !== 'new',
    target: singleInvoiceFetchDataFx,
});

sample({
    clock: CreateInvoiceGate.open,
    target: [fetchOptionFx, fetchCompanyNameFx, fetchTaxCategodyFx, fetchPaymentMeansFx],
});

updateInvoiceStatusFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});

updateInvoiceStatusFx.done.watch((reportId) => {
    setNewInvoiceIsDone(true);
    const setPath = generatePath(
        (history.location.state as {from: string | undefined})?.from ?? '/documents',
    );
    history.push(setPath);
    toast.success(i18next.t('pages.documents.invoices.statusChanged', {reportId}), {
        className: 'Toastify__toast__wide',
    });
});

$newInvoiceIsDone.on(setNewInvoiceIsDone, (_, data) => data);

const invoiceStatusInfoGuard = guard({
    clock: $invoiceState,
    filter: (i: string | null) => !!i && i === 'new',
});
sample({
    clock: invoiceStatusInfoGuard,
    fn: () => ({side: 'OUTGOING', status: DocumentStatus['DRAFT']} as InvoiceStatusInfo),
    target: $invoiceStatusInfo,
});

$invoiceStatusInfo
    .on(singleInvoiceFetchDataFx.doneData, (_, data) => {
        const {side, status} = data.content[0] as DocumentsGridRawRowExtended;
        return {
            side,
            status,
        };
    })
    .reset(CreateInvoiceGate.close);

updateInvoiceBodyFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});

updateInvoiceBodyFx.done.watch((data) => {
    const {reportId} = data.result.data.data;

    setInvoiceCreatingPending(true);
    setNewInvoiceIsDone(true);
    const setPath = generatePath(
        (history.location.state as {from: string | undefined})?.from ?? '/documents',
    );
    history.push(setPath);
    toast.success(i18next.t('pages.documents.form.documentUpdated', {number: reportId}), {
        className: 'Toastify__toast__wide',
    });
});
